import { useMsal } from '@azure/msal-react';
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData,
} from '@tanstack/react-query';
import { apiJson, type HttpError } from './request-service';
import {
  type CustomerSource,
  DEFAULT_SOURCE,
  customersApiBase,
} from './customer-source';

import { useCurrentUserContext } from './current-user-context';

import type { AccessMap } from '../pages/customers/access-map';
import { sortParams, type SortState } from './table-sort';
import type { ChangeLogRow } from './use-changelog';

/**
 * Recipe step 3 — gate the FETCH on the same grant the server gates on, in the
 * HOOK so no caller can forget it. Both tabs are read-only, so reach is the
 * whole question and there is nothing else to check.
 */
const DOWNLINE_TAB = 'customers.detail.downline';
const TIMELINE_TAB = 'customers.detail.timeline';

/**
 * The customer-detail Summary tab. The Notes card and the Renewal card are
 * blocks ON that tab, so their fetches take Summary's grant — the same id the
 * routes' preHandlers gate on.
 */
const SUMMARY_TAB = 'customers.detail.summary';

/** The notes COLLECTION, qualified `<pageId>.<list>` — one projection (Q34). */
const NOTES_LIST = 'customers.detail.notes';

// API response shapes (snake_case to match what the api returns from
// app.customers_v1 — no client-side renaming pass).

export type CustomerListRow = {
  customer_id:           number;
  field1:                string | null;
  field3:                string | null;
  // field12 Shop domain (e.g. "acme.myshopify.com") — used to deep-link the
  // Shopify customer id to that customer's Shopify admin page.
  field12:               string | null;
  first_name:            string;
  last_name:             string;
  company:               string | null;
  customer_type_id:      number;
  customer_type:         string | null;
  customer_status_id:    number;
  customer_status:       string | null;
  rank_id:               number | null;
  rank:                  string | null;
  main_city:             string | null;
  main_state:            string | null;
  main_country:          string | null;
  // Joined in via app.customers_with_dq_summary_v1; null when the
  // customer has no open data-quality issues.
  dq_active_issues:      number | null;
  dq_max_severity:       'blocker' | 'warning' | 'info' | null;
};

export type CustomerDetail = CustomerListRow & {
  /**
   * Per-field access sidecar. Optional and additive — the row stays flat, so
   * every existing field read is unaffected. Only RESTRICTIONS are present, so
   * an Admin's map is empty and an unconverted/older server omits it entirely.
   */
  _access?:              AccessMap;
  email:                 string;
  birth_date:            string | null;        // ISO datetime
  currency_code:         string;
  main_address1:         string;
  main_address2:         string | null;
  main_address3:         string;
  main_zip:              string;
  main_county:           string;
  mail_address1:         string;
  mail_address2:         string | null;
  mail_address3:         string;
  mail_city:             string;
  mail_state:            string;
  mail_zip:              string;
  mail_country:          string;
  mail_county:           string;
  payable_type_id:       number;
  tax_code:              string | null;
  sales_tax_code:        string | null;
  incorporated:          string | null;   // CustomerExtended group 1, Field2 (read-only)
  place_of_birth:        string | null;   // CustomerExtended group 1, Field1 (read-only)
  country_of_birth:      string | null;   // CustomerExtended group 1, Field9 (read-only)
  country_of_nationality: string | null;  // CustomerExtended group 1, Field10 (read-only)
  maiden_name:           string | null;   // CustomerExtended group 1, Field8 (read-only)
  tax_filed_city:        string | null;   // CustomerExtended group 1, Field3 (read-only)
  contrat:               string | null;   // CustomerExtended group 1, Field4 (read-only)
  department:            string | null;   // CustomerExtended group 1, Field7 (read-only)
  max_club:              string | null;   // CustomerExtended group 1, Field11 (read-only)
  customer_track:        string | null;   // CustomerExtended group 1, Field12 (read-only)
  business_tax_id:       string | null;   // CustomerExtended group 1, Field5 "Tax ID Number" (0214)
  qst:                   string | null;   // CustomerExtended group 1, Field6 (0214)
  disallow_direct_deposit_prompt: string | null; // CustomerExtended group 1, Field13 — '0'/'1' (0214)
  date1:                 string | null;   // Upgrade Date (YYYY-MM-DD)
  date2:                 string | null;   // Reinstatement Date (YYYY-MM-DD)
  date4:                 string | null;   // Last Renewal Date (YYYY-MM-DD)
  date5:                 string | null;   // Reversion Date (YYYY-MM-DD)
  tax_code_type_id:      number;
  is_email_subscribed:   boolean | null;
  is_sms_subscribed:     boolean | null;
  phone:                 string;
  mobile_phone:          string;
  phone2:                string;
  email_unsubscribe_date: string | null;  // ISO date (YYYY-MM-DD)
  gender:                string;
  language_id:           number | null;
  language:              string | null;   // language_description, joined from app.languages
  // Tax-registration custom fields (Exigo). field4 VAT, field5 GST,
  // field8 NZBN/ABN, field9 RFC ID. field3 Shopify customer id.
  // (field12 Shop domain is on CustomerListRow.)
  //
  // field4 is VAT per Exigo's own CustomerFieldNames.Field4DisplayName.
  // The `vat_registration` column it replaced is empty for every customer
  // in Exigo itself — see migration 0235 and #1357.
  field4:                string | null;
  field5:                string | null;
  field8:                string | null;
  field9:                string | null;
  field3:                string | null;
  // Group A flags (Exigo custom fields; UI labels them). Stored as
  // '0'/'1'/'' strings (or mixed for the messy ones).
  field10:               string | null;   // App Hold
  field11:               string | null;   // Payable Hold Reason
  field13:               string | null;   // LeadPoolCustomer
  field6:                string | null;   // GST Yes/No
  field7:                string | null;   // Gatekeeper
  field14:               string | null;   // Questura
  field15:               string | null;   // House Account
  field1:                string | null;   // raw slot (Legacy CUSTOMER id)
  field2:                string | null;   // raw slot (Legacy PARTNER id)
  can_login:             boolean | null;
  login_name:            string | null;
  main_verified:         boolean | null;
  enroller_id:           number | null;
  enroller_name:         string | null;
  sponsor_id:            number | null;
  notes:                 string | null;
  created_date:          string;               // ISO datetime
  modified_date:         string;               // ISO datetime
  modified_by:           string;               // Exigo record-modifier marker
  created_by:            string | null;         // 'Import' / 'API - <acct>' / operator (0213)
  _synced_at:            string;
  _deleted_at:           string | null;
  // Optimistic locking + audit attribution — present on the editable
  // customer detail view.
  dx_version?:           number;
  dx_updated_at?:        string | null;
  dx_updated_by?:        string | null;
  // Current state of the partner-fee Renewal subscription. NULL when
  // the customer has no renewal row (e.g. Type-1 consumers) or when
  // the caller can't view customer.subscription_lifecycle.
  renewal_start_date:    string | null;        // ISO datetime
  renewal_expire_date:   string | null;        // ISO datetime
  renewal_is_active:     boolean | null;
  renewal_modified_date: string | null;        // ISO datetime
  // The SUBSCRIPTION row's version, NOT the customer's (`dx_version` above).
  // The renewal-date editor must pin this one — it writes
  // app.customer_subscriptions (#1100). Seeding the modal from the customer's
  // version would 409 against a number the operator cannot see.
  renewal_dx_version:    number | null;
};

export type Lookups = {
  types:         { id: number; name: string }[];
  statuses:      { id: number; name: string }[];
  ranks:         { id: number; name: string }[];
  payable_types: { id: number; name: string | null }[];
  currencies:    { code: string; name: string | null }[];
  tax_code_types:{ id: number; name: string | null }[];
  languages:     { id: number; name: string | null }[];
  /** ISO-2 code + display name, ordered by Exigo `priority`. #915 Block 2. */
  countries:     { code: string; name: string | null }[];
  /**
   * Payable hold reasons (#915 Block 3). `customers.field11` stores `id` as
   * text; `code` is the operator-facing short form (MAIL / ARO / …).
   */
  payable_hold_reasons: { id: number; code: string; name: string | null }[];
};

const PAGE_SIZE = 100;

/**
 * Cursor-paginated customer list. Wraps useInfiniteQuery so the page
 * gets a "Load more" button rather than prev/next paging.
 *
 * `search` is a customer_id prefix (numeric only) — matches the api shape.
 * Empty / non-numeric search yields zero rows.
 *
 * Reads the live mirror (`/api/customers`). Query keys include the source
 * so caches stay separate.
 */
/**
 * Per-column filter values from the table-header inputs. Empty strings
 * are treated as "no filter" by the api. Sent verbatim — semantics
 * (exact-match / ILIKE substring / ILIKE prefix) live server-side in
 * `apps/admin-api/src/routes/customers.ts`.
 */
export type CustomerColumnFilters = {
  customerId?: string;
  shopifyId?:  string;
  company?:    string;
  firstName?:  string;
  lastName?:   string;
  typeId?:     string;
  rankId?:     string;
  city?:       string;
  state?:      string;
  country?:    string;
};

const COLUMN_FILTER_KEYS: (keyof CustomerColumnFilters)[] = [
  'customerId', 'shopifyId', 'company', 'firstName', 'lastName',
  'typeId',     'rankId',    'city',    'state',     'country',
];

/** Click-to-sort keys — admin-api `CUSTOMER_LIST_SORT`. */
export const CUSTOMER_LIST_SORTS = [
  'customer_id', 'shopify_id', 'company', 'first_name', 'last_name',
  'type', 'rank', 'city', 'state', 'country',
] as const;
export type CustomerListSort = (typeof CUSTOMER_LIST_SORTS)[number];

export function useCustomersList(opts: {
  search?:      string;
  source?:      CustomerSource;
  hasDqIssues?: boolean;
  types?:       readonly string[];
  statuses?:    readonly string[];
  colFilters?:  CustomerColumnFilters;
  /** Click-to-sort; omitted = newest customer first (the server's default). */
  sort?:        SortState<CustomerListSort>;
} = {}) {
  const { instance } = useMsal();
  const sortQs       = opts.sort ? sortParams(opts.sort) : {};
  const search       = (opts.search ?? '').trim();
  const source       = opts.source ?? DEFAULT_SOURCE;
  const hasDqIssues  = opts.hasDqIssues ?? false;
  const types        = opts.types    ?? [];
  const statuses     = opts.statuses ?? [];
  const colFilters   = opts.colFilters ?? {};
  const apiBase = customersApiBase(source);
  // An id for the default order; an opaque `o:<n>` offset for any other.
  type Page = { rows: CustomerListRow[]; nextCursor: number | string | null };
  // Stable cache key — sort to keep order-insensitive equality. Joined
  // string keeps useInfiniteQuery's tuple-shape happy without nesting.
  const typesKey    = [...types].sort().join('|');
  const statusesKey = [...statuses].sort().join('|');
  const colKey      = COLUMN_FILTER_KEYS
    .map((k) => `${k}=${(colFilters[k] ?? '').trim()}`)
    .join('&');
  const sortKey = `${sortQs.sort ?? ''}:${sortQs.dir ?? ''}`;
  return useInfiniteQuery<Page, HttpError, InfiniteData<Page>, [string, CustomerSource, string, boolean, string, string, string, string], number | string | null>({
    queryKey: ['customers', source, search, hasDqIssues, typesKey, statusesKey, colKey, sortKey],
    initialPageParam: null,
    // A re-sort keeps the old rows on screen until the new first page lands.
    placeholderData: (prev) => prev,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(PAGE_SIZE), ...sortQs });
      if (pageParam != null) qs.set('cursor', String(pageParam));
      if (search.length > 0) qs.set('search', search);
      if (hasDqIssues) qs.set('hasDqIssues', 'true');
      for (const t of types)    qs.append('type', t);
      for (const s of statuses) qs.append('status', s);
      for (const k of COLUMN_FILTER_KEYS) {
        const v = (colFilters[k] ?? '').trim();
        if (v.length > 0) qs.set(k, v);
      }
      return apiJson(instance, `${apiBase}?${qs.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
  });
}

export function useCustomer(customerId: string | number | null, opts: { source?: CustomerSource } = {}) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  return useQuery<CustomerDetail, HttpError>({
    queryKey: ['customers', source, 'detail', customerId],
    queryFn: () => apiJson(instance, `${apiBase}/${encodeURIComponent(String(customerId))}`),
    enabled: customerId != null && String(customerId).length > 0,
    staleTime: 60_000,
  });
}

export type UplineRow = {
  depth:           number;
  customer_id:     number;
  first_name:      string;
  last_name:       string;
  // Nulled by field-group RBAC for callers without `customer.address:view`.
  main_country:    string | null;
  rank:            string | null;
  rank_id:         number | null;
  // Volumes are NUMERIC in PG → strings on the wire. The UI parses
  // when it needs to render numerically. Null when there's no row in
  // period_volumes for that customer + period combination.
  vol3_current:    string | null;
  vol8_current:    string | null;
  vol3_previous:   string | null;
  vol8_previous:   string | null;
};

/**
 * Recursive walk up the enroller_id chain. Cached short (30s) since the
 * tree structure changes rarely. Disabled when customerId is null or
 * the modal isn't open (pass `enabled: false`).
 */
export function useCustomerUpline(
  customerId: string | number | null,
  opts: { source?: CustomerSource; enabled?: boolean } = {},
) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  return useQuery<{ rows: UplineRow[] }, HttpError>({
    queryKey: ['customers', source, 'upline', customerId],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/upline`,
    ),
    enabled: (opts.enabled ?? true) && customerId != null && String(customerId).length > 0,
    staleTime: 30_000,
  });
}

// Read-only enroller move history for the edit-enroller modal. Driven by
// exigo_mirror.enroller_tree_history; `changed_by` is resolved server-side via
// a ±1h join to customer_change_log (null → unknown). Newest-first, capped
// (default 50) with `total` for the "showing X of Y" footer.
export type EnrollerHistoryRow = {
  move_date:       string;        // ISO datetime
  old_enroller_id: number | null;
  old_level:       number | null;
  reason:          string | null;
  changed_by:      string | null;
};

export function useEnrollerHistory(
  customerId: string | number | null,
  opts: { source?: CustomerSource; enabled?: boolean } = {},
) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  return useQuery<{ rows: EnrollerHistoryRow[]; total: number }, HttpError>({
    queryKey: ['customers', source, 'enroller-history', customerId],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/enroller-history`,
    ),
    enabled: (opts.enabled ?? true) && customerId != null && String(customerId).length > 0,
    staleTime: 30_000,
  });
}

export type DownlineRow = {
  depth:           number;
  customer_id:     number;
  field2:          string | null;
  first_name:      string;
  last_name:       string;
  // Nulled by field-group RBAC for callers without `customer.address:view`.
  main_country:    string | null;
  customer_type:   string | null;
  customer_status: string | null;
  rank:            string | null;
  rank_id:         number | null;
  enroller_id:     number | null;
  created_date:    string | null;
  // #1373: the four volume columns the upline modal shows, same resolver.
  // numeric → string from pg; NULL when the member has no period_volumes row.
  vol3_current:    string | null;
  vol8_current:    string | null;
  vol3_previous:   string | null;
  vol8_previous:   string | null;
};

export type DownlineFilters = {
  depth?:           string;
  customer_id?:     string;
  main_country?:    string;
  name?:            string;
  customer_status?: string;
  /** rank_id as a string — NOT the short code the table displays. */
  rank?:            string;
  /** customer_type_id as a string: '1' Retail, '3' Partner. */
  customer_type_id?: string;
  /** YYYY-MM-DD; matches the calendar day, not an instant. */
  created_date?:    string;
  /** 1-based page number. Not a filter — excluded from the count query's key. */
  page?:            string;
  /** Rows per request. The SPA fetches a BLOCK of pages, so this is the block
   *  size, not what the operator sees on screen. */
  pageSize?:        string;
  /**
   * Click-to-sort column and direction (admin-api `DOWNLINE_SORT`). Not
   * filters: left out of the count, which is the same in any order. Absent =
   * tree order.
   */
  sort?:            string;
  dir?:             string;
};

/** Downline click-to-sort keys — admin-api `DOWNLINE_SORT` (the column keys). */
export const DOWNLINE_SORTS = [
  'depth', 'customer_id', 'name', 'customer_type', 'rank', 'main_country',
  'customer_status', 'created_date',
  'vol3_current', 'vol8_current', 'vol3_previous', 'vol8_previous',
] as const;
export type DownlineSort = (typeof DOWNLINE_SORTS)[number];

export type DownlineResponse = {
  rows:       DownlineRow[];
  truncated:  boolean;
  maxRows:    number;
  /** True when the server applied filters — the client must not re-filter. */
  filtered:   boolean;
  /** Deepest level in the subtree — a bound for the Lvl box, not a list. */
  maxDepth:   number | null;
  /** 1-based page number this response represents. */
  page:       number;
  pageSize:   number;
  /** A further page exists. Cheap lookahead — NOT a total (see useCustomerDownlineCount). */
  hasMore:    boolean;
};

/**
 * Recursive walk down `enroller_id`. Server caps depth and total rows;
 * `truncated` flag tells the SPA to surface a "showing first N" notice
 * for top partners with very large trees.
 *
 * Filters are applied SERVER-side (#1232). They must be: the row cap means
 * client-side filtering would only ever search the first `maxRows` rows, so on
 * a large downline "LVL = 5" would silently mean "level-5 members among the
 * first 1000" — a wrong answer wearing a truncation notice.
 */
export function useCustomerDownline(
  customerId: string | number | null,
  opts: { source?: CustomerSource; filters?: DownlineFilters; enabled?: boolean } = {},
) {
  const mayReach = useCurrentUserContext().reachesTab(DOWNLINE_TAB);
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  const filters = opts.filters ?? {};
  // Sorted so the query key is stable regardless of key order, and empty
  // values are dropped so "typed then cleared" returns to the cached unfiltered
  // result rather than issuing a distinct request.
  const params = new URLSearchParams(
    Object.entries(filters)
      .filter(([, v]) => typeof v === 'string' && v.trim() !== '')
      .map(([k, v]): [string, string] => [k, (v as string).trim()])
      .sort((a, b) => a[0].localeCompare(b[0])),
  );
  const qs = params.toString();
  return useQuery<DownlineResponse, HttpError>({
    queryKey: ['customers', source, 'downline', customerId, qs],
    queryFn: () =>
      apiJson(
        instance,
        `${apiBase}/${encodeURIComponent(String(customerId))}/downline${qs ? `?${qs}` : ''}`,
      ),
    enabled: (opts.enabled ?? true) && mayReach && customerId != null && String(customerId).length > 0,
    staleTime: 30_000,
    // Keep showing the previous rows while a filter change is in flight, so the
    // table does not blank out on every keystroke.
    placeholderData: (prev) => prev,
  });
}

/**
 * Total rows matching the CURRENT filters, for the page count and the
 * last-page jump.
 *
 * Separate from the rows query on purpose: the count walks the whole filtered
 * subtree (~1.1s on a 951k-row downline) while a page turn is ~130ms. Paying it
 * per page would undo pagination. So the table pages on `hasMore`, and this
 * fills in the total when it arrives.
 *
 * ⚠️ Must be given the SAME filters as the rows query, or the pager is sized
 * from a different result set than it pages through.
 */
export function useCustomerDownlineCount(
  customerId: string | number | null,
  opts: { source?: CustomerSource; filters?: DownlineFilters; enabled?: boolean } = {},
) {
  const mayReach = useCurrentUserContext().reachesTab(DOWNLINE_TAB);
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  const filters = opts.filters ?? {};
  const params = new URLSearchParams(
    Object.entries(filters)
      .filter(([k, v]) => k !== 'page' && k !== 'sort' && k !== 'dir' && typeof v === 'string' && v.trim() !== '')
      .map(([k, v]): [string, string] => [k, (v as string).trim()])
      .sort((a, b) => a[0].localeCompare(b[0])),
  );
  const qs = params.toString();
  return useQuery<{ total: number }, HttpError>({
    queryKey: ['customers', source, 'downline-count', customerId, qs],
    queryFn: () =>
      apiJson(
        instance,
        `${apiBase}/${encodeURIComponent(String(customerId))}/downline/count${qs ? `?${qs}` : ''}`,
      ),
    enabled: (opts.enabled ?? true) && mayReach && customerId != null && String(customerId).length > 0,
    // Longer than the rows' 30s: a subtree size does not move page to page, and
    // this is the expensive query of the pair.
    staleTime: 120_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch the WHOLE filtered downline for a CSV export, capped server-side.
 *
 * Not a hook: an export is an event, not state. Called on click so nothing is
 * fetched until the operator asks — the response can be 50,000 rows.
 */
export async function fetchCustomerDownlineExport(
  instance: ReturnType<typeof useMsal>['instance'],
  customerId: string | number,
  opts: { source?: CustomerSource; filters?: DownlineFilters } = {},
): Promise<{ rows: DownlineRow[]; truncated: boolean; maxRows: number }> {
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  const filters = opts.filters ?? {};
  const params = new URLSearchParams(
    Object.entries(filters)
      // page / pageSize describe the on-screen page, never the export.
      .filter(([k, v]) => k !== 'page' && k !== 'pageSize'
        && typeof v === 'string' && v.trim() !== '')
      .map(([k, v]): [string, string] => [k, (v as string).trim()])
      .sort((a, b) => a[0].localeCompare(b[0])),
  );
  const qs = params.toString();
  return apiJson(
    instance,
    `${apiBase}/${encodeURIComponent(String(customerId))}/downline/export${qs ? `?${qs}` : ''}`,
  );
}

export type DownlineFacets = {
  countries: string[];
  statuses:  string[];
  rank_ids:  number[];
};

/**
 * Distinct Country / Status / Rank across the WHOLE subtree, for the downline
 * filter dropdowns.
 *
 * Deliberately a SEPARATE request from the rows, and `enabled` only when the
 * row list came back truncated: the aggregate costs ~730ms on a large subtree,
 * and when nothing was truncated the returned rows already contain every value,
 * so the client can derive the options for free. This fetch runs in the
 * background — the table renders on the rows-derived options and the dropdowns
 * widen when it lands.
 */
export function useCustomerDownlineFacets(
  customerId: string | number | null,
  opts: { source?: CustomerSource; enabled?: boolean } = {},
) {
  const mayReach = useCurrentUserContext().reachesTab(DOWNLINE_TAB);
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  return useQuery<DownlineFacets, HttpError>({
    queryKey: ['customers', source, 'downline-facets', customerId],
    queryFn: () =>
      apiJson(
        instance,
        `${apiBase}/${encodeURIComponent(String(customerId))}/downline/facets`,
      ),
    enabled: mayReach && (opts.enabled ?? false) && customerId != null && String(customerId).length > 0,
    // The subtree's shape changes far more slowly than its rows, and this is
    // the expensive call — keep it for the session rather than per navigation.
    staleTime: 5 * 60_000,
  });
}

export type RenewalOrderRow = {
  customer_id:      number;
  order_id:         number;
  order_date:       string;          // ISO datetime
  item_id:          number;
  item_description: string;
  days_each:        number;
};

/**
 * Per-customer Renewal credit-order history. One row per order line whose
 * item carries a partner kit SKU (`PARTNER_KIT_SKUS`, `@commissions/reference`);
 * each row credits `days_each` days. Result is small per customer (~1-10
 * rows); no pagination.
 */
export function useCustomerRenewalOrders(
  customerId: string | number | null,
  opts: { source?: CustomerSource } = {},
) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  return useQuery<{ rows: RenewalOrderRow[] }, HttpError>({
    queryKey: ['customers', source, 'renewal-orders', customerId],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/renewal-orders`,
    ),
    enabled: customerId != null && String(customerId).length > 0,
    staleTime: 30_000,
  });
}

export type RenewalAdjustmentRow = {
  id:           string;         // uuid
  days:         number;         // signed
  reason:       string;
  isSystem:     boolean;
  createdBy:    string | null;  // null on system baseline rows
  createdByUpn: string | null;
  createdAt:    string;         // ISO datetime
};

/**
 * Manual renewal-day adjustments (#1100 — app.customer_renewal_adjustments).
 * Rendered in the Renewal card alongside the credit-granting orders: an
 * adjustment is a peer of those rows, crediting or debiting days with a reason
 * and an author.
 *
 * camelCase, NOT snake_case: this route selects through Drizzle, so the JSON
 * carries the model's field names verbatim rather than the column names — the
 * same trap that made every note's author read "unknown" (see CustomerNote).
 */
export function useCustomerRenewalAdjustments(
  customerId: string | number | null,
  opts: { source?: CustomerSource } = {},
) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  // Don't fire a request the caller's grants already refuse — recipe step 3.
  // An affordance, not the enforcement: the server refuses regardless. What
  // this prevents is a red 403 in the console behind a correct-looking screen.
  const { reachesTab } = useCurrentUserContext();
  const mayReach = reachesTab(SUMMARY_TAB);
  return useQuery<{ rows: RenewalAdjustmentRow[] }, HttpError>({
    queryKey: ['customers', source, 'renewal-adjustments', customerId],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/renewal-adjustments`,
    ),
    enabled: customerId != null && String(customerId).length > 0 && mayReach,
    staleTime: 30_000,
  });
}

// Discrete operator note (#829 — app.customer_notes). Append-only; the legacy
// Exigo `notes` blob on the customer detail is separate (rendered read-only
// alongside these).
// camelCase, NOT snake_case: this route selects through Drizzle, so the JSON
// carries the model's field names verbatim rather than the column names. The
// type declared snake_case, which cannot be caught by tsc — it is a hand-written
// shape, so every field simply read `undefined` at runtime and the author line
// rendered "unknown" on every note.
export type CustomerNote = {
  id:           string;         // uuid
  body:         string;
  // Pinned notes render as one standing block above the chronological log
  // (0236). The BODY is still append-only — this is the only mutable thing
  // about a note.
  pinned:       boolean;
  // Always present. A note written over HTTP carries the caller's oid; a note
  // minted by the legacy-pin backfill carries the oid of the OPERATOR who ran
  // that script (it refuses to apply without a signed-in human). There is no
  // authorless note.
  createdBy:    string;         // Entra oid
  createdByUpn: string | null;  // readable author label
  createdAt:    string;         // ISO datetime
  // Optimistic lock. Sent back on the pin PATCH; a stale one is refused 409.
  dxVersion:    number;
};

/** Per-customer discrete notes, newest-first. */
export function useCustomerNotes(
  customerId: string | number | null,
  opts: { source?: CustomerSource } = {},
) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  // Recipe step 3, on the LISTS axis (Q34): don't enumerate a collection the
  // caller's grants already refuse. Not the Summary tab's grant — reaching a
  // tab and enumerating a collection on it are independent questions, which is
  // exactly what lets a role read the notes without being able to add one.
  const { reachesList } = useCurrentUserContext();
  const mayReach = reachesList(NOTES_LIST);
  return useQuery<{ notes: CustomerNote[] }, HttpError>({
    queryKey: ['customers', source, 'notes', customerId],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/notes`,
    ),
    enabled: customerId != null && String(customerId).length > 0 && mayReach,
    staleTime: 30_000,
  });
}

/**
 * Pin or unpin one note (0236).
 *
 * The caller passes the `dxVersion` it last saw. A stale one comes back 409 —
 * someone else pinned or unpinned the same note first. There is no Was/Now
 * diff to render here (a pin has no authored value to compare, only a state
 * that already lost), so the honest recovery is to refetch and let the operator
 * see the state that won. `onSettled` invalidates on both paths for exactly
 * that reason.
 */
export function useSetNotePinned(
  customerId: string | number,
  opts: { source?: CustomerSource } = {},
) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  return useMutation<
    { ok: boolean; note: CustomerNote },
    HttpError,
    { noteId: string; pinned: boolean; dxVersion: number }
  >({
    mutationFn: ({ noteId, pinned, dxVersion }) => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/notes/${noteId}`,
      { method: 'PATCH', body: JSON.stringify({ pinned, dxVersion }) },
    ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['customers', source, 'notes', customerId] });
    },
  });
}

/**
 * Per-customer timeline: the global change-log feed for this one customer,
 * every customer-scoped change-log, rows shaped as on the Change Log page.
 * Default limit 200, newest first.
 */
export function useCustomerTimeline(
  customerId: string | number | null,
  opts: { source?: CustomerSource; limit?: number } = {},
) {
  const mayReach = useCurrentUserContext().reachesTab(TIMELINE_TAB);
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const limit  = opts.limit  ?? 200;
  const apiBase = customersApiBase(source);
  return useQuery<{ rows: ChangeLogRow[] }, HttpError>({
    queryKey: ['customers', source, 'timeline', customerId, limit],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/timeline?limit=${limit}`,
    ),
    enabled: mayReach && customerId != null && String(customerId).length > 0,
    staleTime: 30_000,
  });
}

/**
 * Lookups change rarely; cache aggressively. Used by the list page for the
 * customer-type filter legend (when we add filtering) and as the source
 * of truth for status/rank tone mapping.
 *
 * Lookup tables live in `exigo_mirror`. The `source`-keyed caching is
 * here so each surface has its own cache slot; the underlying data is
 * the same.
 */
export function useCustomerLookups(opts: { source?: CustomerSource } = {}) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = customersApiBase(source);
  return useQuery<Lookups, HttpError>({
    queryKey: ['customers', source, 'lookups'],
    queryFn: () => apiJson(instance, `${apiBase}/lookups`),
    staleTime: 5 * 60_000,
  });
}
